import * as React from 'react';
import camelCase from 'lodash/camelCase';
import omit from 'lodash/omit';
import { ThemeContext } from 'styled-components';

import { IMakeErrorMsgID, makeErrorMsgID, VALID_EDGE_STYLE_DATA, VALID_MEDIA_STYLES } from '../../Utils';
import { FormError, FormHelperTxt, FormLegend } from '../FormStyles';
import { IErrorMsgs } from '../common';
import { RadioGroup } from '../RadioInputs/RadioGroup/RadioGroup.style';
import { IRadioGroupProps } from '../RadioInputs/RadioGroup/RadioGroup.type';

import { FieldSet } from './FieldSetGroupStyles';
import { IFieldSetCommonProps } from './common';

export interface ILegendProps extends Omit<React.HTMLAttributes<HTMLLegendElement>, 'title'> {
  title: string | React.ReactNode | JSX.Element;
}

export interface IFieldSetGroupProps extends IErrorMsgs, IFieldSetCommonProps {
  formErrorProps?: React.HTMLAttributes<HTMLElement>;
  fieldSetGroupProps?: IRadioGroupProps;
  legendProps: ILegendProps;
  name: string;
  margin?: VALID_EDGE_STYLE_DATA;
  order?: VALID_MEDIA_STYLES;
  alignSelf?: VALID_MEDIA_STYLES;
  flexBasis?: VALID_MEDIA_STYLES;
  flexGrow?: VALID_MEDIA_STYLES;
  flexShrink?: VALID_MEDIA_STYLES;
  helperText?: string;
}
export interface IFieldSetGroupContext {
  hasError: boolean;
  errorIds: string;
  groupName: string;
  helperTextId?: string;
}
const FieldSetGroupContext: React.Context<IFieldSetGroupContext> = React.createContext<IFieldSetGroupContext>({
  hasError: false,
  errorIds: '',
  groupName: '',
  helperTextId: undefined,
});

export const useFieldSetGroupContext = () => React.useContext(FieldSetGroupContext);

const FieldSetGroup: React.FC<IFieldSetGroupProps> = (
  {
    alignSelf,
    errorMsgs,
    children,
    flexBasis,
    flexGrow,
    flexShrink,
    formErrorProps,
    fieldSetGroupProps,
    helperText,
    legendProps,
    margin,
    order,
    /*
    name is munged to force the name prop to be pass
    via inputProps when using with single checkboxes
    */
   name: groupName,
  }) => {

    let isInputRadioFieldOrCheckbox = false;
    let isInputRadioFieldCard = false;

    React.Children.forEach(children as JSX.Element | JSX.Element[], (child: JSX.Element ) => {
      if (
        child &&
        child.type.displayName &&
        (child.type.displayName.includes('InputRadioField') || child.type.displayName === 'InputCheckboxField')
      ) {
        isInputRadioFieldOrCheckbox = true;
      }

      if (child.type.displayName === 'InputRadioField.card') {
        isInputRadioFieldCard = true;
      }
    });

    const { themeTwo: { form: { errorText: { checkboxAndRadioErrorIndention }}}} = React.useContext(ThemeContext);
    const errorMsgControls: IMakeErrorMsgID | null = errorMsgs ? makeErrorMsgID(errorMsgs, groupName) : null;
    const hasError: boolean = !!errorMsgControls;
    const errorIds: string = errorMsgControls ? errorMsgControls.errorMsgIds.join(' ') : '';
    const hasInlineDisplay = !!(fieldSetGroupProps && fieldSetGroupProps.inline);
    const canIndentError = !!(checkboxAndRadioErrorIndention && groupName && isInputRadioFieldOrCheckbox);
    const legendHTMLAttrs = legendProps && omit(legendProps, 'title');
    const helperTextId: string | undefined = helperText ? camelCase(`${legendProps.title} helper text`) : undefined;

    return (

      <FieldSet
        alignSelf={alignSelf}
        flexBasis={flexBasis}
        flexGrow={flexGrow}
        flexShrink={flexShrink}
        marginProp={margin}
        orderProp={order}
        isRadioCard={isInputRadioFieldCard}
      >
        <FormLegend {...legendHTMLAttrs}>{legendProps.title}</FormLegend>
          <RadioGroup {...fieldSetGroupProps}>
            <FieldSetGroupContext.Provider value={{ hasError, errorIds, groupName, helperTextId }}>
              {children}
            </FieldSetGroupContext.Provider>
          </RadioGroup>

        {helperText && (
          <FormHelperTxt id={helperTextId} canIndentHelperText={canIndentError}>
            {helperText}
          </FormHelperTxt>
        )}

        {errorMsgControls && (
          errorMsgControls.errorMessages.map((msg, index) => (
            <FormError
              canIndentError={canIndentError}
              hasInlineDisplay={hasInlineDisplay}
              id={errorMsgControls.errorMsgIds[index]}
              key={msg}
              {...formErrorProps}
            >
              {msg}
            </FormError>
          )
        ))}
      </FieldSet>
    );
};

export default FieldSetGroup;
